import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { gql, useMutation } from '@apollo/client'
import { useTranslation } from 'react-i18next'
import { Modal } from 'antd'

import ScmFinishedOrderRender from './render'

const UPDATE_SCM_FINISHED_ORDER = gql`
	mutation UpdateScmFinishedOrder($orderId: ID!, $finished: Boolean, $completed: Boolean) {
		updateScmFinishedOrder(orderId: $orderId, finished: $finished, purchaseOrderCompleted: $completed) {
			order {
				id
				number
        scmFinished
        scmPurchaseOrderCompleted
			}
			errors
		}
	}
`

const parseErrors = (data) => {
  const { errors: response } = data ? data.updateScmFinishedOrder : {}
  const errors = JSON.parse(response || '{}').order || []

  return errors
}

const parseStates = (data) => {
  const { order: response } = data ? data.updateScmFinishedOrder : {}
  return response ? {
    finished: response.scmFinished,
    completed: response.scmPurchaseOrderCompleted
  } : {}
}

const ScmFinishedOrder = ({ order }) => {
  const { i18n } = useTranslation()
  const { finished: defaultFinished = false, purchase_order_completed: defaultCompleted = false } = order.scm_finished_order || {}
  const [completed, setCompleted] = useState(defaultCompleted)
  const [finished, setFinished] = useState(defaultFinished)

  const [saveScmFinishedOrder, { data, loading: saving }] = useMutation(UPDATE_SCM_FINISHED_ORDER)

  const errors = parseErrors(data)
  const states = parseStates(data)

  useEffect(() => {
    if (states.finished !== undefined) {
      setFinished(states.finished)
    }
    if (states.completed !== undefined) {
      setCompleted(states.completed)
    }
    // Depend on the primitive values, not the freshly-built `states` object
    // (which has a new identity every render and would re-run this needlessly).
  }, [states.finished, states.completed])

  const updateSCMFinishedOrder = ({ finished, completed }) => {
    saveScmFinishedOrder({ variables: { orderId: order.id, finished, completed } })
  }

  if (!order.payment_time) { return null }

  const onCheckScmFinishedOrder = (event) => {
    const value = event.target.checked

    updateSCMFinishedOrder({ finished: value })
  }
  
  const onCheckScmPurchaseOrderCompleted = (event) => {
    const value = event.target.checked

    // Unchecking, or checking before the "finished" prerequisite is met, fires
    // the mutation directly so the backend returns its validation error in red
    // immediately — matching the original behavior. The confirmation only makes
    // sense once the action would actually go through and emit the order's
    // documents to osh-account (which requires `finished` first).
    if (!value || !finished) {
      updateSCMFinishedOrder({ completed: value })
      return
    }

    // Checking this box emits the order's documents to osh-account and can no
    // longer be edited, so confirm before firing the mutation. The checkbox is
    // controlled, so cancelling leaves it unchecked without further handling.
    Modal.confirm({
      title: i18n.t('orders:scmCompleteConfirm.title'),
      content: i18n.t('orders:scmCompleteConfirm.content'),
      okText: i18n.t('orders:scmCompleteConfirm.ok'),
      cancelText: i18n.t('orders:scmCompleteConfirm.cancel'),
      onOk: () => updateSCMFinishedOrder({ completed: true })
    })
  }

  return (
      <ScmFinishedOrderRender
        saving={saving}
        scmFinishedOrder={{
          onChange: onCheckScmFinishedOrder,
          checked: finished
        }}
        scmPurchaseOrderCompleted={{
          onChange: onCheckScmPurchaseOrderCompleted,
          checked: completed
        }}
        errors={errors} />
  )
}

ScmFinishedOrder.propTypes = {
  order: PropTypes.object,
  onSaveSuccess: PropTypes.func
}

ScmFinishedOrder.defaultProps = {
  onSaveSuccess: () => {}
}

export default ScmFinishedOrder
